import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWorkshopById, clearCurrentWorkshop } from '../store/slices/workshopsSlice';
import SEO from '../components/SEO/SEO';
import { fetchSEO } from '../store/slices/seoSlice';
import WorkshopBookingForm from '../components/workshops/WorkshopBookingForm';
import { FaArrowLeft, FaCalendar, FaClock, FaMoneyBillWave, FaUser, FaVideo, FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';

const WorkshopDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentWorkshop, loading } = useSelector((state) => state.workshops);
  const [showBookingForm, setShowBookingForm] = useState(false);

  useEffect(() => {
    dispatch(fetchWorkshopById(id));
    dispatch(fetchSEO('workshop-detail'));
    
    return () => {
      dispatch(clearCurrentWorkshop());
    };
  }, [id, dispatch]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const formatPrice = (price, currency) => {
    if (!price) return 'Free';
    return `${currency} ${price}`;
  };

  const getContentTypeBadge = (contentType) => {
    switch (contentType) {
      case 'live':
        return { text: '🔴 Live', color: 'bg-red-100 text-red-800 border-red-300' };
      case 'recorded':
        return { text: '📹 Recorded', color: 'bg-blue-100 text-blue-800 border-blue-300' };
      case 'both':
        return { text: '🔴📹 Live + Recorded', color: 'bg-purple-100 text-purple-800 border-purple-300' };
      default:
        return { text: 'Workshop', color: 'bg-gray-100 text-gray-800 border-gray-300' };
    }
  };

  const handleBookNow = () => {
    if (!currentWorkshop) return;

    // Check if workshop is full
    if (currentWorkshop.maxParticipants && 
        currentWorkshop.registeredCount >= currentWorkshop.maxParticipants) {
      toast.error('This workshop is fully booked!');
      return;
    }

    // Check if it's a past live workshop (warning only, still allow booking)
    if (currentWorkshop.contentType === 'live' || currentWorkshop.contentType === 'both') {
      const workshopDate = new Date(currentWorkshop.date);
      const now = new Date();
      if (workshopDate < now) {
        toast.warning('This live workshop has already passed. Recorded content may be available.');
      }
    }

    // Open booking form modal
    setShowBookingForm(true);
  };

  const handleBookingSuccess = (bookingData) => {
    // Optionally refresh workshop data to update registered count
    dispatch(fetchWorkshopById(id));
    setShowBookingForm(false);
  };

  if (loading && !currentWorkshop) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-96 bg-gray-200 rounded mb-8" />
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!currentWorkshop) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-gray-600 mb-4">Workshop not found</p>
        <button
          onClick={() => navigate('/workshops')}
          className="text-primary hover:underline"
        >
          Back to Workshops
        </button>
      </div>
    );
  }

  const badge = getContentTypeBadge(currentWorkshop.contentType);
  const isFullyBooked = currentWorkshop.maxParticipants && 
    currentWorkshop.registeredCount >= currentWorkshop.maxParticipants;
  const workshopDate = new Date(currentWorkshop.date);
  const now = new Date();
  const isPast = workshopDate < now;

  return (
    <>
      <SEO
        page="workshop-detail"
        title={currentWorkshop.title}
        description={currentWorkshop.description || currentWorkshop.title}
        keywords={`workshop, ${currentWorkshop.tags?.join(', ') || ''}, ${currentWorkshop.contentType}`}
        image={currentWorkshop.image}
        url={`/workshops/${currentWorkshop._id}`}
      />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6 transition"
        >
          <FaArrowLeft />
          Back
        </button>

        <article className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left Side - Image */}
            {currentWorkshop.image && (
              <div className="relative h-96 lg:h-auto lg:min-h-[600px] overflow-hidden">
                <img
                  src={currentWorkshop.image}
                  alt={currentWorkshop.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium border ${badge.color}`}>
                    {badge.text}
                  </span>
                </div>
              </div>
            )}

            {/* Right Side - Details */}
            <div className="p-6 lg:p-8">
              <h1 className="font-heading text-3xl lg:text-4xl font-bold text-black mb-6">
                {currentWorkshop.title}
              </h1>

              {/* Workshop Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {currentWorkshop.date && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <FaCalendar className="text-primary text-xl" />
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-semibold">{formatDate(currentWorkshop.date)}</p>
                  </div>
                </div>
              )}

              {currentWorkshop.time && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <FaClock className="text-primary text-xl" />
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-semibold">{formatTime(currentWorkshop.time)}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <FaMoneyBillWave className="text-primary text-xl" />
                <div>
                  <p className="text-sm text-gray-600">Price</p>
                  <p className="font-semibold text-primary text-lg">
                    {formatPrice(currentWorkshop.price, currentWorkshop.currency)}
                  </p>
                </div>
              </div>

              {currentWorkshop.instructor && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <FaUser className="text-primary text-xl" />
                  <div>
                    <p className="text-sm text-gray-600">Instructor</p>
                    <p className="font-semibold">{currentWorkshop.instructor}</p>
                  </div>
                </div>
              )}

              {currentWorkshop.duration && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <FaClock className="text-primary text-xl" />
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-semibold">{currentWorkshop.duration}</p>
                  </div>
                </div>
              )}

              {currentWorkshop.maxParticipants && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <FaUsers className="text-primary text-xl" />
                  <div>
                    <p className="text-sm text-gray-600">Participants</p>
                    <p className="font-semibold">
                      {currentWorkshop.registeredCount || 0} / {currentWorkshop.maxParticipants}
                    </p>
                  </div>
                </div>
              )}
            </div>

              {/* Description */}
              <div className="mb-6 pb-6 border-b">
                <h2 className="font-semibold text-xl mb-3">Description</h2>
                <div 
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: currentWorkshop.description }}
                />
              </div>

              {/* Content Type Specific Information */}
              {(currentWorkshop.contentType === 'live' || currentWorkshop.contentType === 'both') && (
                <div className="mb-6 pb-6 border-b">
                  <h2 className="font-semibold text-xl mb-3 flex items-center gap-2">
                    <FaVideo className="text-red-600" />
                    Live Session Details
                  </h2>
                  {currentWorkshop.meetingLink ? (
                    <a
                      href={currentWorkshop.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline break-words"
                    >
                      {currentWorkshop.meetingLink}
                    </a>
                  ) : (
                    <p className="text-gray-600">Meeting link will be provided after booking.</p>
                  )}
                  {isPast && (
                    <p className="text-red-600 text-sm mt-2">
                      ⚠️ This live session has already passed.
                    </p>
                  )}
                </div>
              )}

              {(currentWorkshop.contentType === 'recorded' || currentWorkshop.contentType === 'both') && (
                <div className="mb-6 pb-6 border-b">
                  <h2 className="font-semibold text-xl mb-3 flex items-center gap-2">
                    <FaVideo className="text-blue-600" />
                    Recorded Content
                  </h2>
                  {currentWorkshop.videoUrl ? (
                    <a
                      href={currentWorkshop.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline break-words"
                    >
                      Watch Recording: {currentWorkshop.videoUrl}
                    </a>
                  ) : (
                    <p className="text-gray-600">Video recording will be available after the live session.</p>
                  )}
                </div>
              )}

              {/* Tags */}
              {currentWorkshop.tags && currentWorkshop.tags.length > 0 && (
                <div className="mb-6 pb-6 border-b">
                  <h3 className="font-semibold mb-3 text-black">Tags:</h3>
                  <div className="flex flex-wrap gap-2">
                    {currentWorkshop.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Book Now Button */}
              <div className="mt-8 pt-6 border-t">
                <button
                  onClick={handleBookNow}
                  disabled={isFullyBooked}
                  className={`w-full px-8 py-4 rounded-lg text-lg font-semibold transition ${
                    isFullyBooked
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary-dark'
                  }`}
                >
                  {isFullyBooked ? 'Fully Booked' : 'Book Now'}
                </button>
                {isFullyBooked && (
                  <p className="text-red-600 text-sm mt-2">
                    This workshop is fully booked. Please check other available workshops.
                  </p>
                )}
              </div>

              {/* Back Link */}
              <div className="mt-6">
                <Link
                  to="/workshops"
                  className="text-primary hover:underline flex items-center gap-2"
                >
                  <FaArrowLeft />
                  Back to Workshops
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Booking Form Modal */}
        {currentWorkshop && (
          <WorkshopBookingForm
            workshop={currentWorkshop}
            isOpen={showBookingForm}
            onClose={() => setShowBookingForm(false)}
            onBookingSuccess={handleBookingSuccess}
          />
        )}
      </div>
    </>
  );
};

export default WorkshopDetailPage;

